// components/CategoryTabs.jsx
import React, { useRef, useEffect, useState } from 'react';

const CategoryTabs = ({ categories, selectedCategory, onSelectCategory }) => {
    const scrollRef = useRef(null);
    const activeTabRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    const checkScrollButtons = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
        }
    };

    useEffect(() => {
        checkScrollButtons();
        window.addEventListener('resize', checkScrollButtons);
        return () => window.removeEventListener('resize', checkScrollButtons);
    }, [categories]);

    // Scroll active category into view when selected
    useEffect(() => {
        if (activeTabRef.current && scrollRef.current) {
            activeTabRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center',
            });
            setTimeout(checkScrollButtons, 300);
        }
    }, [selectedCategory]);

    const scrollBy = (amount) => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
            setTimeout(checkScrollButtons, 300);
        }
    };

    // Format category name for display
    const formatCategoryName = (category) => {
        if (category === 'all') return 'All Menu';
        return category.charAt(0).toUpperCase() + category.slice(1);
    };

    return (
        <div className="sticky top-[57px] z-10 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
            <div className="max-w-3xl mx-auto relative group">
                {/* Left fade & arrow */}
                {canScrollLeft && (
                    <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white to-transparent pointer-events-none flex items-center justify-start z-10">
                        <button
                            onClick={() => scrollBy(-200)}
                            aria-label="Scroll left"
                            className="pointer-events-auto ml-2 w-8 h-8 rounded-full bg-white shadow-md border border-gray-200 flex items-center justify-center text-gray-500 hover:text-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all hidden md:flex"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                        </button>
                    </div>
                )}

                <div
                    ref={scrollRef}
                    onScroll={checkScrollButtons}
                    className="flex overflow-x-auto scrollbar-hide space-x-3 px-4 py-4 sm:px-6"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                    {categories.map((category) => {
                        const isActive = selectedCategory === category;
                        return (
                            <button
                                key={category}
                                ref={isActive ? activeTabRef : null}
                                onClick={() => onSelectCategory(category)}
                                className={`
                                    relative px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap
                                    transition-all duration-300 transform active:scale-95
                                    ${isActive
                                        ? 'bg-amber-600 text-white shadow-lg shadow-amber-600/30 ring-2 ring-amber-600 ring-offset-2 ring-offset-white'
                                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-900 border border-gray-200 hover:border-gray-300'
                                    }
                                `}
                            >
                                {formatCategoryName(category)}
                            </button>
                        );
                    })}
                </div>

                {/* Right fade & arrow */}
                {canScrollRight && (
                    <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent pointer-events-none flex items-center justify-end z-10">
                        <button
                            onClick={() => scrollBy(200)}
                            aria-label="Scroll right"
                            className="pointer-events-auto mr-2 w-8 h-8 rounded-full bg-white shadow-md border border-gray-200 flex items-center justify-center text-gray-500 hover:text-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all hidden md:flex"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CategoryTabs;